import { useEffect, useState, useMemo } from "react";
import Card from "../components/Card";
import Button from "../components/Button";
import { loadSession } from "../auth";
import { getTeamProgress } from "../services/api";
import { useTranslation, Trans } from "react-i18next";

function StatusBadge({ status }) {
  const base = "px-2 py-0.5 rounded-full text-xs border font-medium";
  const map = {
    "not-started": `${base} bg-slate-100 border-slate-200 text-slate-600`,
    "in-progress": `${base} bg-amber-50 border-amber-200 text-amber-700`,
    "passed-quiz": `${base} bg-emerald-50 border-emerald-200 text-emerald-700`,
    "awaiting-upload": `${base} bg-purple-50 border-purple-200 text-purple-700`,
    "completed": `${base} bg-emerald-50 border-emerald-200 text-emerald-700`
  };
  const { t } = useTranslation();
  return <span className={map[status] || base}>{t(`status.${status}`)}</span>;
}

export default function Team() {
  const session = loadSession();
  const me = session?.user;
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | due | completed
  const [err, setErr] = useState("");

  async function load() {
    if (!me?.derived?.isManager) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const data = await getTeamProgress();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || t("team.error_generic"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      await load();
      if (!alive) return;
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.derived?.isManager]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let list = rows;
    if (term)
      list = list.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(term) ||
          (r.email || "").toLowerCase().includes(term)
      );
    if (filter === "due") list = list.filter((r) => (r.dueCount || 0) > 0);
    if (filter === "completed")
      list = list.filter((r) => (r.completedCount || 0) > 0);
    return [...list].sort(
      (a, b) => b.dueCount - a.dueCount || (a.name || "").localeCompare(b.name || "")
    );
  }, [rows, q, filter]);

  if (!me?.derived?.isManager) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <Card>
          <div className="text-sm text-slate-600">
            <Trans i18nKey="team.no_reports_tip">
              You don’t have direct reports. To demo this page, log in as <code>manager@acme.com</code> and seed a team.
            </Trans>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <h1 className="text-2xl font-bold text-slate-900">{t("team.title")}</h1>
        <div className="flex gap-2">
          <input
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white text-slate-700 placeholder:text-slate-400"
            placeholder={t("team.search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select
            className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="all">{t("team.filter_all")}</option>
            <option value="due">{t("team.filter_due")}</option>
            <option value="completed">{t("team.filter_completed")}</option>
          </select>
          <Button variant="secondary" onClick={async () => { await load(); }}>
            {t("team.btn_refresh")}
          </Button>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="text-sm text-slate-500">{t("team.loading")}</div>
        ) : err ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{err}</div>
        ) : filtered.length ? (
          <ul className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <li key={r.userId || r.email} className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-semibold text-slate-900">{r.name}</div>
                    <div className="text-sm text-slate-500">{r.email}</div>
                  </div>
                  <div className="text-right text-sm text-slate-500">
                    <div>
                      {t("team.summary_completed")}{" "}
                      <span className="font-semibold text-emerald-700">{r.completedCount || 0}</span>
                    </div>
                    <div>
                      {t("team.summary_due")}{" "}
                      <span className="font-semibold text-red-600">{r.dueCount || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {(r.trainings || []).map((titem) => (
                    <div
                      key={`${r.userId}-${titem.trainingId}`}
                      className="p-3 rounded-xl border border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        <div className="font-medium text-sm text-slate-900">{titem.title}</div>
                        <div className="mt-1.5">
                          <StatusBadge status={titem.status} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-slate-500 py-4 text-center">{t("team.no_members")}</div>
        )}
      </Card>
    </div>
  );
}
